import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import RefreshIcon from "../../../../components/icons/RefreshIcon";
import DisplayContainer from "../../../../components/displayContainer/DisplayContainer";
import Loader from "../../../../components/loader/Loader";
import OneTournament from "../../../../components/oneTournament/OneTournament";
import TextWrapper from "../../../../components/textWrapper/TextWrapper";
import { fetchPaginatedTournament } from "../../../../features/tournaments/tournamentsSlice";
import styles from "./allTournaments.module.css";

const AllTournaments = () => {
  const dispatch = useDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const {
    list: tournaments,
    fetchStatus,
    currentPage,
    totalPages,
  } = useSelector((state) => state.tournaments);

  const tournamentListRef = useRef();

  const handleRefreshTournaments = useCallback(async () => {
    setIsRefreshing(true);
    tournamentListRef.current?.scrollTo?.({
      top: 0,
      behavior: "smooth",
    });

    try {
      await dispatch(fetchPaginatedTournament({ page: 1 })).unwrap();
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch]);

  const displayTournaments = tournaments.map((item) => {
    return (
      <OneTournament
        tournamentId={item._id}
        key={item._id}
        status={item.status}
        name={item.name}
        serialNumber={item.serialNumber}
        createdBy={item.createdBy}
        startDate={item.startDate}
        endDate={item.endDate}
        description={item.description}
        enDescription={item.enDescription}
        prize={item.prize}
        tournamentImage={item.tournamentImage}
        prizeDescription={item.prizeDescription}
        enPrizeDescription={item.enPrizeDescription}
        playersCount={item.playersCount}
        requiredAds={item.requiredAds}
        requiredAdsByRegion={item.requiredAdsByRegion}
        stage={item.stage}
        liveLink={item.liveLink}
        gameName={item.gameName}
        tournamentType={item.tournamentType}
        maxUsersByTeam={item.maxUsersByTeam}
      />
    );
  });

  const handleScroll = useCallback(() => {
    const container = tournamentListRef.current;
    if (!container || fetchStatus === "loading") return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight - 50 &&
      currentPage < totalPages
    ) {
      dispatch(fetchPaginatedTournament({ page: currentPage + 1 }));
    }
  }, [dispatch, fetchStatus, currentPage, totalPages]);

  useEffect(() => {
    if (fetchStatus === "idle") {
      dispatch(fetchPaginatedTournament({ page: 1 }));
    }
  }, [dispatch, fetchStatus]);

  useEffect(() => {
    const container = tournamentListRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [dispatch, currentPage, totalPages, handleScroll]);

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <div className={styles.headerTitleGroup}>
          <TextWrapper textStyle="textTitle">جميع البطولات</TextWrapper>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => {
              Promise.resolve(handleRefreshTournaments()).catch(() => {});
            }}
            disabled={fetchStatus === "loading" || isRefreshing}
            title="تحديث جميع البطولات"
            aria-label="تحديث جميع البطولات"
          >
            {isRefreshing ? (
              <Loader size={18} color="var(--color-accent-primary)" />
            ) : (
              <RefreshIcon className={styles.refreshIcon} />
            )}
          </button>
        </div>
      </div>
      <div className={styles.allAdminsWrapper}>
        <DisplayContainer ref={tournamentListRef}>
          {displayTournaments}
          {fetchStatus === "loading" && (
            <div className={styles.loaderContainer}>
              <Loader />
            </div>
          )}
        </DisplayContainer>
      </div>
    </div>
  );
};

export default AllTournaments;
